import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage, ToolCall } from './types';
import { MONO, FOCUS, MOTION, PANEL_HEADER, PANEL_TITLE } from './styles';
import { buttonClass } from '../components/ui/Button';
import { TextArea } from '../components/ui/Field';
import Tag from '../components/ui/Tag';
import LogoSpinner from '../components/ui/LogoSpinner';
import Tooltip from '../components/ui/Tooltip';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';

interface AnalysisChatProps {
  messages: ChatMessage[];
  onSend: (message: string) => void;
  onStop?: () => void;
  isAnalyzing: boolean;
  currentToolCall?: string;
  rateLimitCountdown?: number;
  logId: string;
  selectedSerials?: Record<string, string>;
  timeWindow?: { start: number; end?: number } | null;
  onFocusTimeChange?: (t: number | null) => void;
}

/* ── Parse "T+3:45", "t≈23.0s", "~210s" into seconds ── */
function parseTimestamp(text: string): number | null {
  // T+M:SS or T+H:MM:SS
  let m = text.match(/[Tt]\+(\d+):(\d{2})(?::(\d{2}))?/);
  if (m) {
    if (m[3]) return parseInt(m[1]) * 3600 + parseInt(m[2]) * 60 + parseInt(m[3]);
    return parseInt(m[1]) * 60 + parseInt(m[2]);
  }
  // T+23.0s or t≈23.0s (seconds value)
  m = text.match(/[Tt][+≈](\d+\.?\d*)s/);
  if (m) return parseFloat(m[1]);
  // ~210s
  m = text.match(/~(\d+\.?\d*)s/);
  if (m) return parseFloat(m[1]);
  return null;
}

/* ── Markdown renderer (inline: bold, italic, code, bullet, tables) ── */
const renderMarkdown = (text: string, onFocusTimeChange?: (t: number | null) => void): React.ReactNode => {
  if (!text) return null;

  const lines = text.replace(/<[^>]*>/g, '').split('\n');
  const elements: React.ReactNode[] = [];
  let inCodeBlock = false;
  let codeBlockLines: string[] = [];
  let inTable = false;
  let tableRows: string[][] = [];

  const processInline = (line: string, key: number): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    // Extended regex: markdown first, then timestamps
    // Timestamps: T+3:45, T+210s, t≈23.0s, ~210s — require 's' suffix or M:SS to avoid false positives
    const regex = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|[Tt]\+\d+:\d{2}(?::\d{2})?|[Tt][+≈]\d+\.?\d*s|~\d+\.?\d*s\b)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let k = 0;

    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) parts.push(line.slice(lastIndex, match.index));
      const m = match[0];
      if (m.startsWith('`')) {
        parts.push(
          <code key={k++} className={`rounded bg-surface-container-highest px-1 py-px text-[0.9em] text-on-surface ${MONO}`}>
            {m.slice(1, -1)}
          </code>
        );
      } else if (m.startsWith('**')) {
        parts.push(<strong key={k++} className="font-semibold text-on-surface">{m.slice(2, -2)}</strong>);
      } else if (m.startsWith('*')) {
        parts.push(<em key={k++} className="italic">{m.slice(1, -1)}</em>);
      } else {
        // Timestamp — make hoverable
        const seconds = parseTimestamp(m);
        if (seconds != null && onFocusTimeChange) {
          parts.push(
            <Tooltip
              key={k++}
              label={`Point at or focus to show T+${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')} on the charts`}
            >
              <span
                tabIndex={0}
                onMouseEnter={() => onFocusTimeChange(seconds)}
                onMouseLeave={() => onFocusTimeChange(null)}
                onFocus={() => onFocusTimeChange(seconds)}
                onBlur={() => onFocusTimeChange(null)}
                className={`cursor-crosshair rounded-sm border-b border-dashed border-primary/40 text-[0.92em] text-primary tabular-nums ${MONO} ${FOCUS}`}
              >
                {m}
              </span>
            </Tooltip>
          );
        } else {
          parts.push(m);
        }
      }
      lastIndex = match.index + m.length;
    }
    if (lastIndex < line.length) parts.push(line.slice(lastIndex));
    return <span key={key}>{parts}</span>;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Code blocks
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={i} className={`my-1.5 overflow-x-auto rounded bg-surface-container-highest px-2.5 py-2 text-xs leading-normal text-on-surface ${MONO}`}>
            {codeBlockLines.join('\n')}
          </pre>
        );
        codeBlockLines = [];
      }
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) { codeBlockLines.push(line); continue; }

    // Tables
    if (line.includes('|') && line.trim().startsWith('|')) {
      const cells = line.split('|').slice(1, -1).map(c => c.trim());
      if (cells.every(c => /^[-:]+$/.test(c))) continue; // separator row
      if (!inTable) inTable = true;
      tableRows.push(cells);
      continue;
    }
    if (inTable) {
      elements.push(
        <div key={`tbl-${i}`} className="my-1.5 min-w-0 overflow-x-auto rounded">
          <table className="w-full border-collapse text-xs tabular-nums">
            <thead>
              <tr>
                {tableRows[0]?.map((h, j) => (
                  <th key={j} className="whitespace-nowrap bg-surface-container-high px-2 py-1.5 text-left font-medium text-on-surface-variant">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.slice(1).map((row, ri) => (
                <tr key={ri} className="even:bg-surface-container">
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-2 py-1 text-on-surface">
                      {processInline(cell, ci)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      inTable = false;
      tableRows = [];
    }

    // Headers
    if (line.startsWith('### ')) {
      elements.push(<div key={i} className="mb-1 mt-2.5 text-sm font-semibold text-on-surface">{line.slice(4)}</div>);
      continue;
    }
    if (line.startsWith('## ')) {
      elements.push(<div key={i} className="mb-1 mt-3 text-[15px] font-semibold text-on-surface text-balance">{line.slice(3)}</div>);
      continue;
    }
    if (line.startsWith('# ')) {
      elements.push(<div key={i} className="mb-1.5 mt-3.5 text-base font-bold text-on-surface text-balance">{line.slice(2)}</div>);
      continue;
    }

    // Horizontal rule
    if (/^[-*_]{3,}$/.test(line.trim())) {
      elements.push(<hr key={i} className="my-2 border-0 border-t border-outline-variant" />);
      continue;
    }

    // Bullets
    if (line.match(/^\s*[-*]\s/)) {
      const indent = line.match(/^(\s*)/)?.[1].length || 0;
      elements.push(
        <div key={i} className="relative my-0.5" style={{ paddingLeft: `${12 + indent * 8}px` }}>
          <span aria-hidden="true" className="absolute text-on-surface-variant" style={{ left: `${indent * 8}px` }}>&bull;</span>
          {processInline(line.replace(/^\s*[-*]\s/, ''), i)}
        </div>
      );
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      elements.push(<div key={i} className="h-1.5" />);
      continue;
    }

    // Regular text
    elements.push(<div key={i} className="my-0.5 leading-normal">{processInline(line, i)}</div>);
  }

  // Flush trailing table
  if (inTable && tableRows.length > 0) {
    elements.push(
      <div key="tbl-end" className="my-1.5 min-w-0 overflow-x-auto rounded">
        <table className="w-full border-collapse text-xs tabular-nums">
          <tbody>
            {tableRows.map((row, ri) => (
              <tr key={ri} className="even:bg-surface-container">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-2 py-1 text-on-surface">
                    {processInline(cell, ci)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return <>{elements}</>;
};

/* ── Tool call display ── */
const ToolCallIndicator: React.FC<{ calls: ToolCall[] }> = ({ calls }) => {
  const [expanded, setExpanded] = useState(false);
  if (!calls || calls.length === 0) return null;

  return (
    <div className="mb-1.5">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className={`flex w-full min-w-0 items-center gap-1.5 rounded px-2 py-1 text-xs text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface ${FOCUS} ${MOTION}`}
      >
        <svg
          width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"
          className={`flex-shrink-0 transition-transform duration-150 ease-emphasized motion-reduce:transition-none ${expanded ? 'rotate-90' : ''}`}
        >
          <path fill="currentColor" d="M3 1.5 7.5 5 3 8.5z" />
        </svg>
        <span className="tabular-nums">{calls.length} tool call{calls.length !== 1 ? 's' : ''}</span>
        <span className={`ml-auto tabular-nums ${MONO}`}>
          {calls.reduce((sum, c) => sum + (c.response_size || 0), 0).toLocaleString()} chars
        </span>
      </button>
      <Reveal show={expanded}>
        <div className="pl-5 pt-1">
          {calls.map((call, i) => (
            <div key={i} className={`flex min-w-0 gap-2 py-0.5 text-xs ${MONO}`}>
              <span className="min-w-[120px] truncate text-on-surface">{call.tool_name}</span>
              <span className="tabular-nums text-on-surface-variant">
                {call.response_size ? `${call.response_size.toLocaleString()} chars` : ''}
              </span>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  );
};

/* ── Main Chat Component ── */
const AnalysisChat: React.FC<AnalysisChatProps> = ({
  messages, onSend, onStop, isAnalyzing, currentToolCall, rateLimitCountdown, logId, selectedSerials, timeWindow, onFocusTimeChange,
}) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isAnalyzing]);

  const handleSubmit = () => {
    const trimmed = input.trim();
    if (!trimmed || isAnalyzing) return;
    onSend(trimmed);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const rateLimited = !!rateLimitCountdown && rateLimitCountdown > 0;
  const formatT = (s: number) => `T+${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-surface-container-low">
      {/* Chat header with selected serials */}
      <div className={`${PANEL_HEADER} flex-col items-stretch`}>
        <div className="flex min-w-0 items-center justify-between gap-2">
          <h2 className={PANEL_TITLE}>Conversation</h2>
          <Appear show={messages.length > 0} className={`whitespace-nowrap text-xs text-on-surface-variant tabular-nums ${MONO}`}>
            {messages.length} messages
          </Appear>
        </div>
        <Reveal show={!!selectedSerials && Object.keys(selectedSerials).length > 0}>
          <div className="mt-1.5 flex min-w-0 flex-wrap gap-1.5">
            {Object.entries(selectedSerials ?? {}).map(([id, serial]) => (
              <Tag key={id} variant="code" mono>
                {serial}
              </Tag>
            ))}
            {timeWindow && (
              <Tag variant="code" mono>
                Window {formatT(timeWindow.start)} to {timeWindow.end != null ? formatT(timeWindow.end) : 'end'}
              </Tag>
            )}
          </div>
        </Reveal>
      </div>

      {/* Messages */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden px-4 py-3">
        {messages.length === 0 && !isAnalyzing && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-5 py-10 text-center">
            <p className="max-w-[280px] text-sm leading-relaxed text-on-surface-variant">
              {logId
                ? `Ask about the selected flight${logId.includes(',') ? 's' : ''}, or ask for a full analysis.`
                : 'Pick a flight from the list, paste its id, or upload a log.'}
            </p>
            <div className="text-xs leading-loose text-on-surface-variant">
              <p className="font-medium text-on-surface">Try asking</p>
              <p>Analyze this flight log</p>
              <p>What caused the voltage drop at T+120s?</p>
              <p>Compare ESC 3 to the other motors</p>
              <p>Is this battery safe to fly again?</p>
            </div>
          </div>
        )}

        {messages.map(msg => (
          <div key={msg.id} className={`flex min-w-0 max-w-full flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
            {msg.role === 'user' ? (
              <div className="max-w-[90%] whitespace-pre-wrap break-words rounded-lg bg-surface-container-high px-3 py-2 text-sm leading-normal text-on-surface">
                {msg.content}
              </div>
            ) : (
              <div className="w-full min-w-0 max-w-full">
                {msg.toolCalls && <ToolCallIndicator calls={msg.toolCalls} />}
                <div className={`break-words text-sm leading-normal ${msg.role === 'system' ? 'rounded bg-surface-container px-3 py-2 text-on-surface-variant' : 'text-on-surface'}`}>
                  {renderMarkdown(msg.content, onFocusTimeChange)}
                </div>
                {msg.usage && (
                  <div className={`mt-1 flex flex-wrap gap-x-3 text-[11px] text-on-surface-variant tabular-nums ${MONO}`}>
                    <span>{msg.iterations} steps</span>
                    <span>{msg.usage.input_tokens?.toLocaleString()} tokens in</span>
                    <span>{msg.usage.output_tokens?.toLocaleString()} out</span>
                    {msg.duration_s && <span>{Math.round(msg.duration_s)}s</span>}
                    {msg.usage.total_cost != null && <span>${msg.usage.total_cost.toFixed(3)}</span>}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Analyzing indicator */}
        <Reveal show={isAnalyzing}>
          <div role="status" className="flex flex-col gap-1.5 rounded-lg bg-surface-container px-3 py-2.5">
            <div className="flex min-w-0 items-center gap-2">
              {rateLimited ? (
                <span aria-hidden="true" className="h-2 w-2 flex-shrink-0 rounded-full ring-2 ring-inset ring-amber-400" />
              ) : (
                <LogoSpinner size="inline" className="flex-shrink-0" />
              )}
              <span className={`min-w-0 flex-1 text-sm tabular-nums ${rateLimited ? 'text-amber-400' : 'text-on-surface'}`}>
                {rateLimited ? (
                  <>
                    Rate limited, resuming shortly
                    {/* the ticking number stays out of the live region, or a reader hears it every second */}
                    <span aria-hidden="true">{` (${Math.floor(rateLimitCountdown! / 60)}:${String(rateLimitCountdown! % 60).padStart(2, '0')})`}</span>
                  </>
                ) : 'Analyzing...'}
              </span>
              {onStop && (
                <button type="button" onClick={onStop} className={buttonClass('danger', 'sm')}>
                  Stop
                </button>
              )}
            </div>
            <Reveal show={!!currentToolCall}>
              <div className={`min-w-0 truncate pl-[26px] text-xs text-on-surface-variant ${MONO}`}>
                {currentToolCall}
              </div>
            </Reveal>
            <Reveal show={!rateLimited}>
              <div className="pl-[26px] text-xs text-on-surface-variant">
                The analyst is reading the log and running checks.
              </div>
            </Reveal>
          </div>
        </Reveal>

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 bg-surface-container p-3">
        <div className="flex min-w-0 items-end gap-2">
          <TextArea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isAnalyzing || !logId}
            placeholder={logId ? 'Ask about this flight' : 'Pick a flight first'}
            aria-label="Question for the analyst"
            rows={1}
            className="max-h-[120px] min-h-[40px] flex-1 !resize-none leading-snug"
            onInput={(e) => {
              const el = e.target as HTMLTextAreaElement;
              el.style.height = 'auto';
              el.style.height = Math.min(el.scrollHeight, 120) + 'px';
            }}
          />
          <Tooltip label="Send with Enter. Shift+Enter starts a new line.">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isAnalyzing || !input.trim() || !logId}
              className={buttonClass('filled')}
            >
              {isAnalyzing ? 'Analyzing' : 'Send'}
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default AnalysisChat;
